"use client";

import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../UI/select";

export default function SimpleRevenueChart({
monthlyData,
weeklyData
}: any) {
  const [period, setPeriod] = useState("monthly");
  const [metric, setMetric] = useState("ingresos");

  const data = period === "monthly" ? monthlyData : weeklyData;
  const maxValue = Math.max(
    ...data.map((item : any) => item[metric as keyof (typeof data)[0]] as number)
  );

  // Función para calcular el porcentaje de altura basado en el valor máximo
  const getHeight = (value: number) => {
    const maxWidthPx = 300;
    const pixelValue = (value / maxValue) * maxWidthPx;
    return `${pixelValue}px`;
    return `${(value / maxValue) * 100}%`;
  };

  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between mb-[45px]">
        <h3 className="text-lg font-semibold">Análisis de Ingresos</h3>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Mensual</SelectItem>
              <SelectItem value="weekly">Semanal</SelectItem>
            </SelectContent>
          </Select>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ingresos">Ingresos</SelectItem>
              <SelectItem value="comisiones">Comisiones</SelectItem>
              <SelectItem value="reservas">Reservas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Simple Chart Implementation */}
      <div className="h-[300px] relative">
        <div className="absolute inset-0 flex items-end">
          <div className="w-full flex items-end justify-between h-[250px]">
            <div key={-1} className="flex flex-col items-center group">
              <div className="text-xs text-gray-600 mb-1 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className={`w-8  rounded-t-sm relative group`}>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"></div>
              </div>
              <div className="text-xs text-gray-600 mt-1 rotate-45 origin-left"></div>
            </div>
            {data.map((item : any, index : any) => {
              const value = item[metric as keyof typeof item] as number;
              return (
                <div key={index} className="flex flex-col items-center group">
                  <div className="text-xs text-gray-600 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {metric === "ingresos" || metric === "comisiones"
                      ? `$${value.toLocaleString()}`
                      : value}
                  </div>
                  <div
                    className={`w-8 ${
                      metric === "reservas" ? "bg-[#8B9456]" : "bg-[#8B9456]"
                    } rounded-t-sm relative group`}
                    style={{ height: getHeight(value) }}
                  >
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {metric === "ingresos" || metric === "comisiones"
                        ? `$${value.toLocaleString()}`
                        : value}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 mt-1 rotate-45 origin-left">
                    {period === "monthly" ? item.month : item.week}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 py-2">
          <div>${maxValue.toLocaleString()}</div>
          <div>${Math.round(maxValue * 0.75).toLocaleString()}</div>
          <div>${Math.round(maxValue * 0.5).toLocaleString()}</div>
          <div>${Math.round(maxValue * 0.25).toLocaleString()}</div>
          <div>0</div>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <div className="font-medium">Resumen:</div>
        <div className="mt-1">
          {metric === "ingresos" && (
            <p>
              Los ingresos más altos se registraron en{" "}
              {data.reduce(
                (max : any, item : any) => (item.ingresos > max.ingresos ? item : max),
                data[0]
              ).month ||
                data.reduce(
                  (max : any, item : any) => (item.ingresos > max.ingresos ? item : max),
                  data[0]
                ).week}{" "}
              con $
              {Math.max(...data.map((item : any) => item.ingresos)).toLocaleString()}.
            </p>
          )}
          {metric === "comisiones" && (
            <p>
              Las comisiones más altas se registraron en{" "}
              {data.reduce(
                (max : any, item : any) => (item.comisiones > max.comisiones ? item : max),
                data[0]
              ).month ||
                data.reduce(
                  (max : any, item : any) =>
                    item.comisiones > max.comisiones ? item : max,
                  data[0]
                ).week}{" "}
              con $
              {Math.max(
                ...data.map((item : any) => item.comisiones)
              ).toLocaleString()}
              .
            </p>
          )}
          {metric === "reservas" && (
            <p>
              El mayor número de reservas se registró en{" "}
              {data.reduce(
                (max : any, item : any) => (item.reservas > max.reservas ? item : max),
                data[0]
              ).month ||
                data.reduce(
                  (max : any, item : any) => (item.reservas > max.reservas ? item : max),
                  data[0]
                ).week}{" "}
              con {Math.max(...data.map((item : any) => item.reservas))} reservas.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
